import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { AssociationsAndSuppliers } from '@/components/AssociationsAndSuppliers'
import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'

// Types for images
interface ImageItem {
  src: string
  alt: string
}

export default function FencesGallery() {
  // State for images
  const [displayedImages, setDisplayedImages] = useState<ImageItem[]>([])
  
  // State for lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxSlide, setLightboxSlide] = useState(0)
  const [lightboxAnimation, setLightboxAnimation] = useState('entering') // 'entering', 'visible', 'exiting'
  
  // Handle client-side navigation
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault()
    window.history.pushState({}, '', href)
    window.dispatchEvent(new Event('popstate'))
    window.scrollTo(0, 0)
  }

  // Function to parse image filename into alt text
  function parseImageFileName(filename: string): string {
    // Remove file extension
    const withoutExtension = filename.substring(0, filename.lastIndexOf('.'))
    // Replace hyphens with spaces
    return withoutExtension.replace(/-/g, ' ')
  }

  // Function to fix path for production and development consistency
  function fixImagePath(path: string): string {
    // Remove /public prefix if it exists (for dev mode)
    let imagePath = path.replace('/public', '')
    
    // Ensure path starts with a slash
    if (!imagePath.startsWith('/')) {
      imagePath = '/' + imagePath
    }
    
    return imagePath
  }

  // Load images dynamically
  useEffect(() => {
    try {
      // Using Vite's import.meta.glob to get all images
      const imageModules = import.meta.glob('/public/assets/images/Fence/**/*.{jpg,jpeg,png,webp}', { eager: true })
      
      const loadedImages: ImageItem[] = []

      // Convert the modules to ImageItem objects
      Object.keys(imageModules).forEach(path => {
        const imagePath = fixImagePath(path)
        
        // Get filename for alt text
        const fileName = path.substring(path.lastIndexOf('/') + 1)
        const altText = parseImageFileName(fileName)
        
        loadedImages.push({
          src: imagePath,
          alt: altText
        })
      })

      setDisplayedImages(loadedImages)
    } catch (error) {
      console.error('Error loading images:', error)
    }
  }, [])
  
  // Lightbox functions
  const openLightbox = (index: number) => {
    setLightboxSlide(index)
    setLightboxOpen(true)
    setLightboxAnimation('entering')
    // Start animation
    setTimeout(() => setLightboxAnimation('visible'), 50)
    // Prevent body scrolling when lightbox is open
    document.body.style.overflow = 'hidden'
  }
  
  const closeLightbox = () => {
    // Start exit animation
    setLightboxAnimation('exiting')
    // Wait for animation to finish before fully closing
    setTimeout(() => {
      setLightboxOpen(false)
      // Restore body scrolling
      document.body.style.overflow = 'visible'
    }, 300) // Should match the animation duration
  }
  
  const goToPrevLightboxSlide = () => {
    setLightboxSlide((prev) => 
      prev === 0 ? displayedImages.length - 1 : prev - 1
    )
  }
  
  const goToNextLightboxSlide = () => {
    setLightboxSlide((prev) => 
      (prev + 1) % displayedImages.length
    )
  }
  
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen) return
      
      switch (e.key) {
        case 'ArrowLeft':
          goToPrevLightboxSlide()
          break
        case 'ArrowRight':
          goToNextLightboxSlide()
          break
        case 'Escape':
          closeLightbox()
          break
        default:
          break
      }
    }
    
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightboxOpen, displayedImages.length])

  return (
    <main>
      <Helmet>
        <title>Fence Photo Gallery | Busy Beaver Construction | Calgary</title>
        <meta name="description" content="View our gallery of custom fence projects in Calgary featuring cedar, pressure-treated, and vinyl fences. Browse designs for privacy, security, and curb appeal." />
        <meta name="keywords" content="fence gallery, fence photos, custom fences Calgary, cedar fences, pressure-treated fences, privacy fences, decorative fences, fence designs" />
        <link rel="canonical" href="https://busybeaverconstruction.com/gallery/fences" />
        <meta property="og:title" content="Fence Photo Gallery | Busy Beaver Construction" />
        <meta property="og:description" content="Browse our portfolio of custom-built fences in Calgary featuring various styles, materials, and designs to enhance your property's privacy and appearance." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://busybeaverconstruction.com/gallery/fences" />
        <meta property="og:image" content="https://busybeaverconstruction.com/assets/images/Fence/Cedar-Custom-Horizontal-fence-1x6-_-1x2-pickets.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="preload" href="/assets/images/Fence/Cedar-Custom-Horizontal-fence-1x6-_-1x2-pickets.jpg" as="image" />
      </Helmet>
      <Header />
      
      {/* Hero Banner */}
      <section className="relative h-[400px] flex items-center justify-center">
        <img 
          src="/assets/images/Fence/Cedar-Custom-fence-1x6-pickets-with-1x2-Horizontal-Custom-Lattice-top.jpg"
          alt="Custom cedar fence with 1x6 pickets and 1x2 horizontal custom lattice top by Busy Beaver Construction Calgary"
          className="absolute inset-0 w-full h-full object-cover loading-eager"
          fetchPriority="high"
        />        
        <div className="relative z-10 mt-64 text-center">
          <div className="inline-block bg-busy-green rounded py-3 mx-4 px-4 lg:px-8">
            <p className="text-white text-base font-medium rounded">Designs To Fit Your Lifestyle</p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center mb-8 md:mb-14">
            <h1 className="text-center text-5xl lg:text-6xl font-primary">Fences and Gate Services</h1>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            <div className="space-y-6 text-base text-gray-500 leading-relaxed">
              <p>
                A well designed fence enhances your landscape as a beautiful backdrop, and creates privacy and security for your backyard space. 
                It adds additional value and extra curb appeal to update the look and feel of your home's exterior.
              </p>
              
              <p>
                We understand the importance of designing all the elements of fence construction to reflect your needs and personality. 
                We are experienced in installing fences in a variety of styles, materials and budget ranges.
              </p>
              
              <p>
                <strong>Wood</strong> – Our solid wood fences provide maximum privacy to your backyard, and are built with the finest 
                Pressure Treated or Cedar fencing products. The most common type of wood fence is the standard vertical picket, Fortress Style. 
                For a more distinctive modern appeal, Horizontal fences offer a great alternative to the traditional style. The style options 
                of a wood fence are endless, and can be custom built to suit your property containment needs.
              </p>
              
              <p>
                <strong>Vinyl</strong> – Although more expensive than other fencing options, a vinyl fence is extremely durable and virtually 
                maintenance free. Vinyl fencing comes in various color and style options which are attractive to the eye, and can easily 
                compliment any home exterior style or color.
              </p>
              
              <p>
                <strong>Chain link</strong> – Generally seen along the back property of a home that faces a green space. Chain link fencing is 
                one of the more popular fencing choices for homeowners on a budget, or not looking to create privacy on their property.
              </p>
              
              <p>
                <strong>Ornamental</strong> – Ornamental fences have been the worldwide architectural choice for attractive security on commercial 
                and residential properties. We install Ameristar Montage line of ornamental fences, which is the leader in residential steel fencing. 
                The maintenance free coatings, and high quality durable construction, gives your property the elegant look of wrought-iron fencing, 
                without the expensive maintenance costs of a traditional wrought iron fence.
              </p>
              
            </div>
            
            <div className="space-y-6">
              <div className="overflow-hidden h-[280px] md:h-[280px]">
                <img 
                  src="/assets/images/Fence/Cedar-Custom-Horizontal-fence-1x6-_-1x2-pickets.jpg"
                  alt="Custom cedar horizontal fence with 1x6 and 1x2 pickets installation by Busy Beaver Construction Calgary"
                  className="w-full h-full object-cover loading-lazy"
                />
              </div>
              <div className="overflow-hidden h-[280px] md:h-[280px]">
                <img 
                  src="/assets/images/2020/04/Decoartive-picket-top-Vinyl-Fence-Chestnut-Brown.jpg"
                  alt="Decorative picket top vinyl fence in Chestnut Brown by Busy Beaver Construction Calgary"
                  className="w-full h-full object-cover loading-lazy"
                />
              </div>
              <div className="overflow-hidden h-[280px] md:h-[280px]">
                <img 
                  src="/assets/images/2020/04/Beige-and-white-Lattice-Top-Vinyl-Fence.jpg"
                  alt="Beige and white lattice top vinyl fence installation by Busy Beaver Construction Calgary"
                  className="w-full h-full object-cover loading-lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Content */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center mb-8 md:mb-14">
            <h1 className="text-center text-5xl lg:text-6xl font-primary">Fence & Gate Gallery</h1>
          </div>
          
          {/* Gallery Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {displayedImages.map((image, index) => (
              <div 
                key={index} 
                className="overflow-hidden rounded shadow-lg hover:shadow-xl transition-shadow duration-300 bg-white cursor-pointer"
                onClick={() => openLightbox(index)}
              >
                <div className="h-[280px] md:h-[320px]">
                  <img 
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover loading-lazy"
                  />
                </div>
                <div className="p-4">
                  <p className="text-sm text-gray-500">{image.alt}</p>
                </div>
              </div>
            ))}
          </div>
          
          {/* No results message */}
          {displayedImages.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-700">Loading images...</p>
            </div>
          )}
          
          <div className="text-center mt-12">
            <p className="mb-6 text-gray-700">
              Ready to enhance your property with a quality fence? Contact us for a free estimate.
            </p>
            <a
              href="/estimate" 
              className="inline-block px-6 py-3 bg-beaver-green text-black font-medium rounded hover:bg-beaver-green-dark hover:brightness-110 transition-all duration-300"
              onClick={(e) => handleNavigation(e, '/estimate')}
            >
              GET FREE ESTIMATE
            </a>
          </div>
        </div>
      </section>
      
      {/* WE DO DAM GOOD WORK Section */}
      <section className="py-10 bg-gray-900 text-white text-center">
        <h2 className="text-4xl md:text-5xl font-bold tracking-wide">
          WE DO <span className="text-red-600">DAM</span> GOOD WORK
        </h2>
      </section>
      
      {/* Associations and Footer */}
      <AssociationsAndSuppliers />
      <Footer />
      
      {/* Lightbox */}
      {lightboxOpen && displayedImages.length > 0 && (
        <div 
          className={`fixed inset-0 bg-black z-50 flex items-center justify-center transition-opacity duration-300 ${
            lightboxAnimation === 'entering' ? 'opacity-0' : 
            lightboxAnimation === 'exiting' ? 'opacity-0' : 'opacity-100'
          }`}
        >
          <div 
            className="absolute inset-0"
            onClick={closeLightbox}
          />
          
          <div className="relative max-w-7xl w-full h-screen max-h-screen flex items-center justify-center p-4">
            {/* Close button */}
            <button 
              onClick={closeLightbox}
              className={`absolute top-4 right-4 z-10 bg-black/50 hover:bg-beaver-green text-white p-3 rounded-full transition-all duration-300 focus:outline-none ${
                lightboxAnimation === 'entering' ? 'opacity-0 translate-y-[-20px]' : 
                lightboxAnimation === 'exiting' ? 'opacity-0 translate-y-[-20px]' : 'opacity-100 translate-y-0'
              }`}
              aria-label="Close lightbox"
            >
              <i className="fas fa-times text-2xl"></i>
            </button>
            
            {/* Image container */}
            <div className="relative w-full h-full flex items-center justify-center">
              <img
                src={displayedImages[lightboxSlide].src}
                alt={displayedImages[lightboxSlide].alt}
                className={`max-w-full max-h-full object-contain transition-all duration-300 ${
                  lightboxAnimation === 'entering' ? 'opacity-0 scale-90' : 
                  lightboxAnimation === 'exiting' ? 'opacity-0 scale-90' : 'opacity-100 scale-100'
                }`}
              />
              
              {/* Image caption */}
              <div 
                className={`absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white py-2 px-4 rounded text-center max-w-lg transition-all duration-300 ${
                  lightboxAnimation === 'entering' ? 'opacity-0 translate-y-[20px]' : 
                  lightboxAnimation === 'exiting' ? 'opacity-0 translate-y-[20px]' : 'opacity-100 translate-y-0'
                }`}
              >
                {displayedImages[lightboxSlide].alt}
              </div>
              
              {/* Navigation buttons */}
              <button 
                onClick={(e) => {
                  e.stopPropagation()
                  goToPrevLightboxSlide()
                }}
                className={`absolute left-[0px] md:left-[60px] lg:left-[-180px] top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-beaver-green text-white p-3 rounded-full transition-all duration-300 focus:outline-none ${
                  lightboxAnimation === 'entering' ? 'opacity-0 translate-x-[-20px]' : 
                  lightboxAnimation === 'exiting' ? 'opacity-0 translate-x-[-20px]' : 'opacity-100 translate-x-0'
                }`}
                aria-label="Previous slide"
              >
                <i className="fas fa-chevron-left text-2xl"></i>
              </button>
              
              <button 
                onClick={(e) => {
                  e.stopPropagation()
                  goToNextLightboxSlide()
                }}
                className={`absolute right-[0px] md:right-[60px] lg:right-[-180px] top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-beaver-green text-white p-3 rounded-full transition-all duration-300 focus:outline-none ${
                  lightboxAnimation === 'entering' ? 'opacity-0 translate-x-[20px]' : 
                  lightboxAnimation === 'exiting' ? 'opacity-0 translate-x-[20px]' : 'opacity-100 translate-x-0'
                }`}
                aria-label="Next slide"
              >
                <i className="fas fa-chevron-right text-2xl"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  )
} 